import { useEffect, useState } from "react";
import client from "../lib/api";
import { money } from "../lib/helpers";
import { Sliders, PencilSimple } from "@phosphor-icons/react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { toast } from "sonner";

export default function PhotographerPayDefaults() {
  const [list, setList] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [editRow, setEditRow] = useState(null);
  const [form, setForm] = useState({ base: "", pct: "", standing: "" });
  const [saving, setSaving] = useState(false);

  const load = () => client.get("/users").then((r) => setList((r.data || []).filter((u) => u.role === "photographer")));
  useEffect(() => { load(); client.get("/tiers").then((r) => setTiers(r.data || [])).catch(() => {}); }, []);

  const tierRate = (name) => { const t = tiers.find((x) => x.name === name); return t ? t.rate : null; };

  const open = (u) => {
    setForm({
      base: u.pay_base_rate != null ? String(u.pay_base_rate) : "",
      pct: u.pay_commission_pct != null ? String(u.pay_commission_pct) : "",
      standing: u.pay_standing_bonus ? String(u.pay_standing_bonus) : "",
    });
    setEditRow(u);
  };

  const save = async () => {
    setSaving(true);
    try {
      await client.patch(`/users/${editRow.id}`, {
        email: editRow.email, name: editRow.name, role: editRow.role, tier: editRow.tier,
        pay_base_rate: form.base === "" ? null : Math.max(0, parseFloat(form.base) || 0),
        pay_commission_pct: form.pct === "" ? null : Math.max(0, parseFloat(form.pct) || 0),
        pay_standing_bonus: form.standing === "" ? 0 : Math.max(0, parseFloat(form.standing) || 0),
      });
      toast.success("Pay defaults updated");
      setEditRow(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save pay defaults"); }
    finally { setSaving(false); }
  };

  if (!list) return null;

  return (
    <div className="border border-[#E9DDC6] rounded-md overflow-hidden bg-white" data-testid="pay-defaults">
      <div className="bg-[#FAF9F6] px-4 py-3 flex items-center gap-2 eyebrow text-[#5C5A58]">
        <Sliders size={16} weight="duotone" />Photographer pay defaults
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[#5C5A58] border-b border-[#E9DDC6]">
            <th className="p-4 font-medium">Photographer</th>
            <th className="p-4 font-medium">Base rate</th>
            <th className="p-4 font-medium">Commission %</th>
            <th className="p-4 font-medium">Standing bonus</th>
            <th className="p-4 font-medium text-right">Edit</th>
          </tr>
        </thead>
        <tbody>
          {list.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-[#5C5A58]">No photographers yet.</td></tr>}
          {list.map((u, i) => (
            <tr key={u.id} className={i % 2 ? "bg-[#FBFAF7]" : "bg-white"} data-testid={`pay-default-${u.id}`}>
              <td className="p-4 font-medium">{u.name || u.email}{u.tier && <span className="text-xs text-[#9A968E] font-normal ml-1">· {u.tier}</span>}</td>
              <td className="p-4 font-mono-num">
                {u.pay_base_rate != null
                  ? <span className="text-[#4A0000] font-semibold">{money(u.pay_base_rate)}</span>
                  : <span className="text-[#5C5A58]">{tierRate(u.tier) != null ? `${money(tierRate(u.tier))} (tier)` : "—"}</span>}
              </td>
              <td className="p-4 font-mono-num">{u.pay_commission_pct != null ? `${u.pay_commission_pct}%` : <span className="text-[#5C5A58]">10% (default)</span>}</td>
              <td className="p-4 font-mono-num">{u.pay_standing_bonus > 0 ? <span className="text-[#8a6d0a]">+{money(u.pay_standing_bonus)}</span> : <span className="text-[#5C5A58]">—</span>}</td>
              <td className="p-4 text-right">
                <Button data-testid={`pay-default-edit-${u.id}`} size="sm" variant="outline" className="border-[#E9DDC6]" onClick={() => open(u)}><PencilSimple size={15} className="mr-1" />Edit</Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <Dialog open={!!editRow} onOpenChange={(o) => !o && setEditRow(null)}>
        <DialogContent data-testid="pay-default-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Pay defaults</DialogTitle>
            <DialogDescription>{editRow?.name || editRow?.email} — set a custom base rate, commission %, and standing bonus. Leave base/commission blank to use the tier rate / default 10%.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-[#5C5A58] mb-1">Custom base rate ($) <span className="text-xs">— blank = use tier rate</span></p>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5A58]">$</span>
                <Input data-testid="pay-default-base" type="number" min="0" step="1" value={form.base} onChange={(e) => setForm((f) => ({ ...f, base: e.target.value }))} className="pl-7" placeholder={editRow && tierRate(editRow.tier) != null ? String(tierRate(editRow.tier)) : "tier rate"} />
              </div>
            </div>
            <div>
              <p className="text-sm text-[#5C5A58] mb-1">Commission % on link bookings <span className="text-xs">— blank = 10%</span></p>
              <div className="relative">
                <Input data-testid="pay-default-pct" type="number" min="0" step="1" value={form.pct} onChange={(e) => setForm((f) => ({ ...f, pct: e.target.value }))} className="pr-7" placeholder="10" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5C5A58]">%</span>
              </div>
            </div>
            <div>
              <p className="text-sm text-[#5C5A58] mb-1">Standing bonus per shoot ($)</p>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5A58]">$</span>
                <Input data-testid="pay-default-standing" type="number" min="0" step="1" value={form.standing} onChange={(e) => setForm((f) => ({ ...f, standing: e.target.value }))} className="pl-7" placeholder="0" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" data-testid="pay-default-cancel" onClick={() => setEditRow(null)}>Cancel</Button>
            <Button data-testid="pay-default-save" disabled={saving} onClick={save} className="bg-[#4A0000] hover:bg-[#5C1A1A] text-white">{saving ? "Saving…" : "Save defaults"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
